import type { ReactNode } from 'react';
import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';

interface KpiCardProps {
  title: string;
  value: string | number | null;
  unit?: string;
  /**
   * この数字がどの期間のものか（'2026-09' / '2026 Q3' 等）。
   * **月次では3枚のカードが別々の月を指す**（退会者数は直近の確定月、オンボは94日前、
   * ロイヤルは187日前）ので、書かないと同じ月の数字が並んでいるように読める。
   */
  period?: string | null;
  trend?: 'improving' | 'stable' | 'declining' | null;
  isAnomaly?: boolean;
  className?: string;
  /** カード下部に描画するチャート */
  chart?: ReactNode;
  /** 見出し右に置く操作要素（期間トグル等）。カード単位で系列を切り替える用途 */
  action?: ReactNode;
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business BentoグリッドのKPIカード。値・単位・トレンド矢印・スパークライン・異常検知バッジを統合表示
 * @graph-connects none
 */
export function KpiCard({
  title,
  value,
  unit,
  period,
  trend,
  isAnomaly,
  className = '',
  chart,
  action,
}: KpiCardProps) {
  const trendIcon = trend === 'improving' ? '\u2191' : trend === 'declining' ? '\u2193' : '\u2192';
  const trendColor =
    trend === 'improving'
      ? 'var(--color-success)'
      : trend === 'declining'
        ? 'var(--color-error)'
        : 'var(--color-text-muted)';

  return (
    <GlassCard padding="none" className={`relative overflow-hidden ${className}`}>
      {isAnomaly && (
        <div className="absolute top-3 right-3">
          <Badge variant="error" size="sm">
            異常検知
          </Badge>
        </div>
      )}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              {title}
            </p>
            {period != null && (
              <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                {period}
              </span>
            )}
          </div>
          {action}
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span
            className="text-3xl font-bold tabular-nums"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {value ?? '\u2014'}
          </span>
          {unit && (
            <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {unit}
            </span>
          )}
          {trend && (
            <span className="text-sm font-medium" style={{ color: trendColor }}>
              {trendIcon}
            </span>
          )}
        </div>
      </div>
      {chart && <div className="px-1 pb-1">{chart}</div>}
    </GlassCard>
  );
}
